import React, { useState } from 'react';
import { ExternalLink, Code, Zap } from 'lucide-react';
import { webPortfolioData } from '../data/webPortfolioData';

const WebPortfolio = () => {
  const [filter, setFilter] = useState('All');

  const categories = ['All', ...new Set(webPortfolioData.map(site => site.category))];

  const filteredSites = filter === 'All' 
    ? webPortfolioData 
    : webPortfolioData.filter(site => site.category === filter);

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-sm z-50 border-b-2 border-lime-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <a href="/" className="text-2xl font-black tracking-wider text-lime-500">
              PETE McGRAW
            </a>
            <div className="hidden md:flex space-x-8">
              <a href="/#about" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                About
              </a>
              <a href="/#ventures" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                Ventures
              </a>
              <a href="/web-portfolio" className="text-lime-500 font-bold uppercase tracking-wide">
                Portfolio
              </a>
              <a href="/#achievements" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                Achievements
              </a>
              <a href="/#contact" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(82, 255, 0, 0.03) 10px, rgba(82, 255, 0, 0.03) 20px)'
        }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="inline-block bg-lime-500 text-black px-4 py-2 font-black text-sm tracking-widest mb-6 transform -skew-x-12">
            <span className="inline-block transform skew-x-12">WEB DEVELOPMENT</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black mb-6 uppercase leading-tight">
            <span className="text-white">SITES</span>
            <br />
            <span className="text-lime-500 block" style={{ textShadow: '0 0 20px rgba(82, 255, 0, 0.5)' }}>PETE BUILT</span>
          </h1>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl font-bold">
            14 websites and apps across enterprise, mobile, and consumer platforms
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`px-6 py-3 font-black uppercase tracking-wide text-sm transition-all transform hover:scale-105 ${
                  filter === category
                    ? 'bg-lime-500 text-black'
                    : 'bg-gray-900 text-white border-2 border-lime-500 hover:bg-lime-500 hover:text-black'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-lime-500 transform origin-bottom-left -skew-y-2"></div>
      </section>

      {/* Portfolio Grid */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredSites.map((site) => (
              <a
                key={site.id}
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gray-900 border-2 border-gray-700 hover:border-lime-500 overflow-hidden transition-all duration-300 transform hover:-translate-y-2"
              >
                {/* Screenshot Preview */}
                <div className="relative h-64 bg-gradient-to-br from-gray-800 to-gray-900 overflow-hidden">
                  {site.screenshot ? (
                    <>
                      <img
                        src={site.screenshot}
                        alt={`${site.name} screenshot`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextElementSibling) {
                            e.target.nextElementSibling.style.display = 'flex';
                          }
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      {/* Fallback content if image fails */}
                      <div className="absolute inset-0 hidden flex-col items-center justify-center text-center p-8" style={{display: 'none'}}>
                        <div className="text-3xl font-bold text-lime-500 mb-3">
                          {site.name}
                        </div>
                        <div className="text-sm text-gray-400 font-medium mb-3">
                          {site.category}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
                        <div className="text-3xl font-bold text-lime-500 mb-3">
                          {site.name}
                        </div>
                        <div className="text-sm text-gray-400 font-medium">
                          {site.category}
                        </div>
                      </div>
                      <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(82, 255, 0, 0.1) 10px, rgba(82, 255, 0, 0.1) 20px)'
                      }}></div>
                    </>
                  )}
                  {site.status && (
                    <div className="absolute top-4 left-4 z-20 bg-black/80 border border-lime-500 text-lime-500 text-xs font-black uppercase tracking-wider px-3 py-1">
                      {site.status}
                    </div>
                  )}
                  <div className="absolute top-4 right-4 z-20 bg-lime-500 p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink className="w-5 h-5 text-black" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="text-xs font-bold text-lime-500 uppercase tracking-wider mb-2">
                        {site.category}
                      </div>
                      <h3 className="text-2xl font-black text-white uppercase group-hover:text-lime-500 transition-colors">
                        {site.name}
                      </h3>
                    </div>
                  </div>

                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {site.description}
                  </p>

                  {/* Tech Stack */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Code className="w-4 h-4 text-lime-500" />
                      <span className="text-xs font-black text-lime-500 uppercase tracking-wider">Tech Stack</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {site.techStack.map((tech, index) => (
                        <span
                          key={index}
                          className="bg-black border border-lime-500 text-lime-500 px-3 py-1 text-xs font-bold uppercase"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-lime-500" />
                      <span className="text-xs font-black text-lime-500 uppercase tracking-wider">Key Features</span>
                    </div>
                    <ul className="space-y-2">
                      {site.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-400 text-sm">
                          <div className="w-1.5 h-1.5 bg-lime-500 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-6 inline-flex items-center gap-2 text-lime-500 font-bold uppercase text-sm group-hover:gap-3 transition-all">
                    <span>View Live Site</span>
                    <ExternalLink className="w-4 h-4" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-gray-900 border-2 border-lime-500 p-8 text-center">
              <div className="text-5xl font-black text-lime-500 mb-2" style={{ textShadow: '0 0 20px rgba(82, 255, 0, 0.5)' }}>
                10+
              </div>
              <div className="text-sm font-bold text-white uppercase tracking-wide">
                Sites Built
              </div>
            </div>
            <div className="bg-gray-900 border-2 border-lime-500 p-8 text-center">
              <div className="text-5xl font-black text-lime-500 mb-2" style={{ textShadow: '0 0 20px rgba(82, 255, 0, 0.5)' }}>
                100%
              </div>
              <div className="text-sm font-bold text-white uppercase tracking-wide">
                Custom Built
              </div>
            </div>
            <div className="bg-gray-900 border-2 border-lime-500 p-8 text-center">
              <div className="text-5xl font-black text-lime-500 mb-2" style={{ textShadow: '0 0 20px rgba(82, 255, 0, 0.5)' }}>
                6+
              </div>
              <div className="text-sm font-bold text-white uppercase tracking-wide">
                Industries
              </div>
            </div>
            <div className="bg-gray-900 border-2 border-lime-500 p-8 text-center">
              <div className="text-5xl font-black text-lime-500 mb-2" style={{ textShadow: '0 0 20px rgba(82, 255, 0, 0.5)' }}>
                10+
              </div>
              <div className="text-sm font-bold text-white uppercase tracking-wide">
                Tech Stacks
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-black uppercase mb-6">
            <span className="text-white">READY TO</span> <span className="text-lime-500">BUILD?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-12 font-bold uppercase">
            Connect with Pete to discuss opportunities
          </p>
          <a
            href="/#contact"
            className="inline-block bg-lime-500 text-black px-12 py-4 font-black text-lg uppercase tracking-wider hover:bg-white transition-all transform hover:scale-105 active:scale-95"
          >
            Get In Touch
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t-2 border-lime-500 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-3xl font-black text-lime-500 mb-4 uppercase">
              Pete McGraw
            </div>
            <p className="text-gray-400 font-medium uppercase tracking-wide text-sm">
              Building the future. One venture at a time.
            </p>
            <div className="mt-6 text-xs text-gray-600 uppercase tracking-wider">
              © 2025 Pete McGraw. All Rights Reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebPortfolio;
